import { useEffect, useState, useRef, useCallback } from "react";
import ChampsProfondIdentification from "@/modules/reeducation/ChampsProfondIdentification";
import ChampsProfondCouleurFeedback from "@/modules/reeducation/ChampsProfondCouleurFeedback";
import ChampsProfondFormesFeedback from "@/modules/reeducation/ChampsProfondFormesFeedback";
import PoursuiteVisuelSmoothV2 from "../occulomotricite/PoursuiteVisuelleSmooth";
import { OptocinetiqueBarresTexteLauncher } from "@/modules/occulomotricite/optocinetiqueTextBarres/OptocinetiqueLauncher";
import { useFullscreen } from "@mantine/hooks";
import EcranInstructions from "@/modules/autres/EcranInstructions";
import Questionnaire from "@/modules/autres/Questionnaire";
import {
  SessionInfo,
  TargetTypeFeedback,
  TargetTypeIdentification,
} from "@/modules/launcher/LauncherModulesPistes";
import LauncherZoneFrontiere from "@/modules/reeducation/LauncherZoneFrontiere";
import { saveResultsModules } from "@/utils/saveResultsModules";
import { ReturnTheRightText } from "@/data/ContenuText";
import EcranInstructionsPreset from "../autres/EcranInscructionsPreset";
import { computeVisibleGrid } from "@/utils/computeVisibleGrid";
import { safeParse } from "@/utils/safeParse";
import { Position as PositionZF } from "@/modules/reeducation/ZoneFrontiere";

// interface UserContextType {
//   caseSizePx?: number;
// }

function getRandomColorFromLine(input: string): string {
  const colorOptions = input.includes("-") ? input.split("-") : [input];
  return colorOptions[Math.floor(Math.random() * colorOptions.length)];
}

const resultFields: Record<string, keyof SessionInfo> = {
  ZF: "resultsZF",
  ZPL: "resultsZPL",
  ZPI: "resultsZPI",
  ZPCCF: "resultsZPCCF",
  ZPFCF: "resultsZPFCF",
  PVS: "resultsPVS",
  OB: "resultsOB",
  OT: "resultsOT",
};

export interface ResultZF {
  position: string;
  reactionTime: number | string;
}

export default function ModuleRenderer({
  sessionInfo,
  // userContext,
  targetZoneFrontiere,
  targetChampsProfondIdentification,
  targetChampsProfondLocalisation,
  targetChampsProfondCouleurFeedback,
  targetsChampProfondFormesTargets,
  casesSelected,
  sessionId,
  role,
  startSession = () => {},
}: {
  sessionInfo: SessionInfo;
  // userContext: UserContextType;
  targetZoneFrontiere: string[];
  targetChampsProfondIdentification: TargetTypeIdentification[];
  targetChampsProfondLocalisation: TargetTypeIdentification[];
  targetChampsProfondCouleurFeedback: TargetTypeFeedback[];
  targetsChampProfondFormesTargets: TargetTypeFeedback[];
  casesSelected: { ZP: string[]; ZF: string[]; ZV: string[] };
  casesSelectedRef: React.MutableRefObject<{
    ZP: string[];
    ZF: string[];
    ZV: string[];
  }>;
  sessionId: string;
  role: "practitioner" | "patient";
  startSession?: () => void;
}) {
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [showInstructions, setShowInstructions] = useState(true);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [endOfModules, setEndOfModules] = useState(false);
  const [showEndOfExerciseScreen, setShowEndOfExerciseScreen] = useState(false);
  const [renderPausedModal, setRenderPausedModal] = useState(false);
  const isPausedAndNotFullScreenRef = useRef(!document.fullscreenElement);
  const caseSize = Number(localStorage.getItem("selectedCaseSizePx"));
  const caseSizeName = localStorage.getItem("selectedCaseSizeName");
  // console.log("Case Size:", caseSize); // Ajout de log
  const { toggle: toggleFullscreen } = useFullscreen();
  const [backgroundColors, setBackgroundColors] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    toggleFullscreen();
  }, []);

  useEffect(() => {
    if (sessionInfo?.parameters) {
      const newBackgroundColors: Record<string, string> = {};

      // Iterate through the session parameters and assign random colors where necessary
      if (sessionInfo.parameters.ZPL?.couleur_fond) {
        newBackgroundColors["ZPL"] = getRandomColorFromLine(
          sessionInfo.parameters.ZPL.couleur_fond
        );
      }
      if (sessionInfo.parameters.ZPI?.couleur_fond) {
        newBackgroundColors["ZPI"] = getRandomColorFromLine(
          sessionInfo.parameters.ZPI.couleur_fond
        );
      }
      // Add more conditions if other modules also have background colors that need randomization

      setBackgroundColors(newBackgroundColors);
    }
  }, [sessionInfo]);
  const resultsZF = safeParse<ResultZF[]>(sessionInfo?.resultsZF ?? "[]") ?? [];
  const targetEnCours_Zf_Bilan =
    safeParse<PositionZF[]>(sessionInfo?.targetEnCours_Zf_Bilan ?? "[]") ?? [];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedModules = sessionInfo?.parameters
    ? [
        {
          sous_module: "ZF",
          title: "Zone Frontiere",
          component: (
            <LauncherZoneFrontiere
              key="ZF"
              caseSize={caseSize}
              isPausedAndNotFullScreenRef={isPausedAndNotFullScreenRef}
              predeterminedCells={targetZoneFrontiere}
              nextModule={() => endModule()}
              inputCaseSession={casesSelected}
              selectionId={sessionInfo.visualFieldSelectionId}
              role={role}
              status={sessionInfo.status}
              initialTargetsLengthEncours_Zf_Bilan={
                sessionInfo.initialTargetsLengthEncours_Zf_Bilan
              }
              targetEnCours_Zf_Bilan={targetEnCours_Zf_Bilan}
              resultsZF={resultsZF}
            />
          ),
          instructions: "ZF",
        },
        {
          sous_module: "ZPL",
          title: "Champs Profond Localisation",
          component: (
            <ChampsProfondIdentification
              key="ZPL"
              caseSize={caseSize}
              isPausedAndNotFullScreenRef={isPausedAndNotFullScreenRef}
              backgroundColor={backgroundColors["ZPL"]}
              targetPositions={targetChampsProfondLocalisation}
              enableBlinkingAndResizing={
                sessionInfo.parameters.ZPL?.mouvement === "true"
              }
            />
          ),
          instructions: "ZPL",
          handleFeedback: "resultsZPL",
          totalDuration: sessionInfo.parameters.ZPL?.duree,
        },
        {
          sous_module: "ZPI",
          title: "Champs Profond Identification",
          component: (
            <ChampsProfondIdentification
              key="ZPI"
              caseSize={caseSize}
              isPausedAndNotFullScreenRef={isPausedAndNotFullScreenRef}
              backgroundColor={backgroundColors["ZPI"]}
              targetPositions={targetChampsProfondIdentification}
              enableBlinkingAndResizing={
                sessionInfo.parameters.ZPI?.mouvement === "true"
              }
            />
          ),
          instructions: "ZPI",
          handleFeedback: "resultsZPI",
          totalDuration: sessionInfo.parameters.ZPI?.duree,
        },
        {
          sous_module: "ZPCCF",
          title: "Champs Profond Couleur Feedback",
          component: (
            <ChampsProfondCouleurFeedback
              key="ZPCCF"
              caseSize={caseSize}
              isPausedAndNotFullScreenRef={isPausedAndNotFullScreenRef}
              targetPositions={targetChampsProfondCouleurFeedback}
              nextModule={() => endModule()}
              totalDuration={parseInt(
                sessionInfo.parameters.ZPCCF?.duree ?? "0",
                10
              )}
              sessionId={sessionId}
              feedbackField="resultsZPCCF"
              role={role}
            />
          ),
          instructions: "ZPCCF",
          // handleFeedback: "resultsZPCCF",
          totalDuration: sessionInfo.parameters.ZPCCF?.duree,
        },
        {
          sous_module: "ZPFCF",
          title: "Champs Profond Formes Feedback",
          component: (
            <ChampsProfondFormesFeedback
              key="ZPFCF"
              caseSize={caseSize}
              isPausedAndNotFullScreenRef={isPausedAndNotFullScreenRef}
              targetPositions={targetsChampProfondFormesTargets}
              nextModule={() => endModule()}
              totalDuration={parseInt(
                sessionInfo.parameters.ZPFCF?.duree ?? "0",
                10
              )}
              sessionId={sessionId}
              feedbackField="resultsZPFCF"
              role={role}
            />
          ),
          instructions: "ZPFCF",
          // handleFeedback: "resultsZPFCF",
          totalDuration: sessionInfo.parameters.ZPFCF?.duree,
        },
        {
          sous_module: "PVS",
          title: "Poursuite Visuelle Smooth",
          component: (
            <PoursuiteVisuelSmoothV2
              key="PVS"
              caseSize={caseSize}
              vitesse={parseInt(sessionInfo.parameters.PVS?.vitesse ?? "1", 10)}
            />
          ),
          instructions: "PVS",
          handleFeedback: "resultsPVS",
          totalDuration: sessionInfo.parameters.PVS?.duree,
        },
        {
          sous_module: "OB",
          title: "Lecture Barres",
          component: (
            <OptocinetiqueBarresTexteLauncher
              key="OB"
              caseSize={caseSize}
              text={ReturnTheRightText(
                sessionInfo.parameters.OB?.contenu ?? ""
              )}
              type="barres"
              wpcm={JSON.parse(sessionInfo.parameters.OB?.vitesse ?? "4")}
            />
          ),
          instructions: "OB",
          handleFeedback: "resultsOB",
          totalDuration: sessionInfo.parameters.OB?.duree,
        },
        {
          sous_module: "OT",
          title: "Lecture Texte",
          component: (
            <OptocinetiqueBarresTexteLauncher
              key="OT"
              caseSize={caseSize}
              text={ReturnTheRightText(
                sessionInfo.parameters.OT?.contenu ?? ""
              )}
              wpcm={JSON.parse(sessionInfo.parameters.OT?.vitesse ?? "4")}
              type="texte"
            />
          ),
          instructions: "OT",
          handleFeedback: "resultsOT",
          totalDuration: sessionInfo.parameters.OT?.duree,
        },
      ].filter((module) => {
        const params =
          sessionInfo.parameters[
            module.sous_module as keyof typeof sessionInfo.parameters
          ];

        const resultKey = resultFields[module.sous_module];
        if (module.sous_module === "ZF") {
          return (
            (params?.selected && sessionInfo[resultKey] === null) ||
            (sessionInfo.targetEnCours_Zf_Bilan !== null &&
              params?.selected &&
              sessionInfo[resultKey] !== null)
          );
        }
        return params?.selected && sessionInfo[resultKey] === null;
      })
    : [];
  const maxCanvasWidth = window.innerWidth;
  const maxCanvasHeight = window.innerHeight;

  const { removedColumns, removedRows } = computeVisibleGrid(
    caseSize,
    maxCanvasWidth,
    maxCanvasHeight
  );

  //   console.log("showEndOfExerciseScreen:", showEndOfExerciseScreen, "endOfModules:", endOfModules, "!sessionIsRealized:", !sessionIsRealized, "currentModuleIndex:", currentModuleIndex, "selectedModules.length:", selectedModules.length);
  const realizeSession = useCallback(() => {
    const fieldsToUpdate = [
      { field: "sessionDate", value: new Date().toISOString() },
      { field: "status", value: "REALISEE" },
    ];
    try {
      // Enregistrer les résultats
      saveResultsModules(fieldsToUpdate, sessionId, role).catch((error) => {
        console.error("An error occurred while saving results:", error);
      });
    } catch (error) {
      console.error("An error occurred while preparing the save:", error);
    }
  }, [
    caseSizeName,
    removedRows,
    removedColumns,
    sessionId,
    role,
    casesSelected,
  ]);

  const nextModule = useCallback(async () => {
    setShowEndOfExerciseScreen(false);
    setCurrentModuleIndex((prevIndex) => {
      const newIndex = (prevIndex + 1) % selectedModules.length;

      if (newIndex === 0) {
        if (prevIndex === selectedModules.length - 1) {
          setShowInstructions(true);
          setEndOfModules(true); // Marquer la fin des modules ici
          return prevIndex; // Arrêter ici après la fin
        }
      }

      return newIndex;
    });

    setShowInstructions(true);
  }, [selectedModules]);

  const endModule = useCallback(() => {
    setShowQuestionnaire(false);
    setShowEndOfExerciseScreen(true);
    if (currentModuleIndex === 0) {
      realizeSession();
    }
  }, [realizeSession, currentModuleIndex]);

  useEffect(() => {
    const simulateKeyPress = (key: string) => {
      const event = new KeyboardEvent("keydown", { key });
      window.dispatchEvent(event);
    };

    const handleFullscreenChange = () => {
      const isFullscreen = !!document.fullscreenElement; // Check if in fullscreen mode
      isPausedAndNotFullScreenRef.current = !isFullscreen;
      setRenderPausedModal(!isFullscreen);

      if (!showInstructions && !showQuestionnaire && isFullscreen) {
        simulateKeyPress("s");
      }
    };

    const handleKeyPress = (e: KeyboardEvent) => {
      if (
        e.key === "ArrowRight" &&
        selectedModules[currentModuleIndex]?.handleFeedback
      ) {
        if (
          !showInstructions &&
          !showQuestionnaire &&
          !showEndOfExerciseScreen
        ) {
          setShowQuestionnaire(true);
        }
      } else if (e.key === "p") {
        toggleFullscreen();
        isPausedAndNotFullScreenRef.current =
          !isPausedAndNotFullScreenRef.current;
      } else if (e.key === " " && isPausedAndNotFullScreenRef.current) {
        toggleFullscreen();
      } else if (e.key === " " && showEndOfExerciseScreen) {
        // Lorsque l'écran de fin d'exercice est affiché et que l'utilisateur appuie sur espace
        // on passe enfin au module suivant
        nextModule();
      } else if (e.key === "t" && !showInstructions && !showQuestionnaire) {
        if (selectedModules[currentModuleIndex]?.handleFeedback) {
          setShowQuestionnaire(true);
        } else {
          // Ici, vérifiez s'il faut passer directement au module suivant ou terminer le module d'abord
          endModule();
        }
      } else if (e.key === "Escape") {
        if (document.fullscreenElement) {
          e.preventDefault();
          document.exitFullscreen();
          isPausedAndNotFullScreenRef.current = true;
        } else {
          isPausedAndNotFullScreenRef.current = true;
        }
      }
    };

    window.addEventListener("fullscreenchange", handleFullscreenChange);
    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("fullscreenchange", handleFullscreenChange);
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [
    toggleFullscreen,
    showInstructions,
    showQuestionnaire,
    currentModuleIndex,
    selectedModules,
    nextModule,
  ]);

  useEffect(() => {
    const currentModule = selectedModules[currentModuleIndex];
    if (
      currentModule?.totalDuration &&
      currentModule.handleFeedback &&
      !showInstructions
    ) {
      const duration =
        parseInt(currentModule?.totalDuration ?? "0", 10) * 1000 * 60; // Convert duration to minutes
      const timer = setTimeout(() => {
        setShowQuestionnaire(true);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [currentModuleIndex, selectedModules, showInstructions]);

  const handleResumeSession = () => {
    // If not in fullscreen, toggle fullscreen
    if (!document.fullscreenElement) {
      toggleFullscreen();
    }
  };

  const renderPauseModal = () => {
    if (renderPausedModal && !showInstructions) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-75">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg text-center">
            <h2 className="text-lg font-semibold">Pause</h2>
            <p className="mt-4">
              Vous êtes en pause. Appuyez sur le bouton ci-dessous pour
              reprendre.
            </p>
            <div className="mt-6 flex justify-center space-x-4">
              <button
                onClick={handleResumeSession}
                className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                ▶️ Reprendre en Plein Écran
              </button>
              <button
                onClick={() => {
                  const event = new KeyboardEvent("keydown", {
                    key: "ArrowRight",
                  });
                  window.dispatchEvent(event);
                  handleResumeSession();
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                ⏭️ Exercice Suivant
              </button>
            </div>

            <p className="mt-4 text-gray-500">
              Ou appuyez sur la barre espace pour continuer.
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (
    !caseSize ||
    caseSize === undefined ||
    caseSize === null ||
    Number(caseSize) === 0
  ) {
    return (
      <EcranInstructions instructions="Veuillez sélectionner une taille de case dans les paramètres avant de commencer le test." />
    );
  }

  const isCursorVisible =
    renderPausedModal || endOfModules || showQuestionnaire
      ? "cursor-pointer"
      : "cursor-none";
  return (
    <div tabIndex={0} style={{ outline: "none" }} className={isCursorVisible}>
      {renderPauseModal()}
      {sessionInfo ? (
        selectedModules.length > 0 ? (
          <div>
            {endOfModules ? (
              <EcranInstructions
                instructions="Exercices terminés. Merci d'avoir réalisé cette séance."
                redirectHome={true}
              />
            ) : showEndOfExerciseScreen ? (
              <EcranInstructions instructions="Fin de l'exercice. Appuyez sur la barre espace pour continuer." />
            ) : (
              <>
                {showInstructions ? (
                  <EcranInstructionsPreset
                    options={{
                      dynamicDisplay: true,
                      fontSizeMultiplier: 1.5,
                      textColor: "white",
                    }}
                    preset={selectedModules[currentModuleIndex].instructions}
                    setShowInstructions={setShowInstructions}
                    startSession={startSession}
                    currentModuleIndex={currentModuleIndex}
                  />
                ) : showQuestionnaire ? (
                  <Questionnaire
                    feedbackType={
                      selectedModules[currentModuleIndex]?.handleFeedback ??
                      "defaultFeedback"
                    }
                    onValidate={endModule}
                    sessionId={sessionId}
                    role={role}
                  />
                ) : (
                  <div>
                    <div>{selectedModules[currentModuleIndex]?.component}</div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <EcranInstructions
            instructions="Aucun exercice n'a été sélectionné lors du paramétrage de la session
            ou tous les exercices ont déjà été réalisés. Veuillez sélectionner
            un exercice lors du paramétrage de la session pour commencer."
            redirectHome={true}
          />
        )
      ) : (
        <p>Chargement des informations de la session...</p>
      )}
    </div>
  );
}
